import React from 'react'
import { Button, Col, Form, Row } from 'react-bootstrap'
import { FaPlus, FaTrash } from 'react-icons/fa'
import {
    IFeed_Input_Rule,
    IFeed_Input_Rule_Cell_Append,
    IFeed_Input_Rule_Cell_EnsureLowest,
    IFeed_Input_Rule_Cell_Replace,
    IFeed_Input_Rule_Column,
    IFeed_Input_Rule_Column_Add,
    IFeed_Input_Rule_Column_Copy,
    IFeed_Input_Rule_Row,
} from './types'

interface FeedRulesFormProps {
    rules: IFeed_Input_Rule[]
    onChange: (updatedRules: IFeed_Input_Rule[]) => void
}

type RuleKind = IFeed_Input_Rule['kind']

const createRule = (kind: RuleKind, type?: string): IFeed_Input_Rule => {
    if (kind === 'column') {
        const columnType = (type || 'add') as IFeed_Input_Rule_Column['type']
        if (columnType === 'add') return { kind, type: 'add', columnName: '', columnValue: '' }
        if (columnType === 'remove') return { kind, type: 'remove', columnName: '' }
        return { kind, type: columnType, columnName: '', newColumnName: '' } as IFeed_Input_Rule
    }

    if (kind === 'cell') {
        const cellType = type || 'append'
        if (cellType === 'ensureLowest') {
            return { kind, type: 'ensureLowest', referenceColumn: '', targetColumn: '' }
        }
        if (cellType === 'replace') {
            return { kind, type: 'replace', columnName: '', fromValue: '', toValue: '' }
        }
        return { kind, type: 'append', columnName: '', content: '' }
    }

    return { kind: 'row', type: 'remove', columnName: '', fromValue: '' }
}

const FeedRulesForm: React.FC<FeedRulesFormProps> = ({ rules, onChange }) => {
    const updateRule = (index: number, updates: Record<string, unknown>) => {
        onChange(rules.map((rule, currentIndex) => (
            currentIndex === index ? { ...rule, ...updates } as IFeed_Input_Rule : rule
        )))
    }

    const replaceRule = (index: number, rule: IFeed_Input_Rule) => {
        onChange(rules.map((currentRule, currentIndex) => currentIndex === index ? rule : currentRule))
    }

    const handleRemoveRule = (index: number) => {
        onChange(rules.filter((_, currentIndex) => currentIndex !== index))
    }

    const renderRuleFields = (rule: IFeed_Input_Rule, index: number) => {
        const fieldId = `feed-rule-${index}`

        if (rule.kind === 'column') {
            return (
                <>
                    <Col xs={12} md={6} lg={4}>
                        <Form.Group controlId={`${fieldId}-column`}>
                            <Form.Label>{rule.type === 'add' ? 'Column name' : 'Source column'}</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="For example: price"
                                value={(rule as IFeed_Input_Rule_Column).columnName || ''}
                                onChange={(event) => updateRule(index, { columnName: event.target.value })}
                                required
                            />
                        </Form.Group>
                    </Col>

                    {rule.type === 'add' && (
                        <Col xs={12} md={6} lg={4}>
                            <Form.Group controlId={`${fieldId}-column-value`}>
                                <Form.Label>Column value</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Static value"
                                    value={(rule as IFeed_Input_Rule_Column_Add).columnValue || ''}
                                    onChange={(event) => updateRule(index, { columnValue: event.target.value })}
                                    required
                                />
                            </Form.Group>
                        </Col>
                    )}

                    {rule.type !== 'remove' && rule.type !== 'add' && (
                        <Col xs={12} md={6} lg={4}>
                            <Form.Group controlId={`${fieldId}-target-column`}>
                                <Form.Label>{rule.type === 'copyIfEmpty' ? 'Fallback column' : 'Target column'}</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="For example: sale_price"
                                    value={(rule as IFeed_Input_Rule_Column_Copy).newColumnName || ''}
                                    onChange={(event) => updateRule(index, { newColumnName: event.target.value })}
                                    required={rule.type === 'copy' || rule.type === 'replace'}
                                />
                            </Form.Group>
                        </Col>
                    )}
                </>
            )
        }

        if (rule.kind === 'cell' && rule.type === 'ensureLowest') {
            return (
                <>
                    <Col xs={12} md={6} lg={4}>
                        <Form.Group controlId={`${fieldId}-reference-column`}>
                            <Form.Label>Reference column</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Column to compare"
                                value={(rule as IFeed_Input_Rule_Cell_EnsureLowest).referenceColumn || ''}
                                onChange={(event) => updateRule(index, { referenceColumn: event.target.value })}
                                required
                            />
                        </Form.Group>
                    </Col>
                    <Col xs={12} md={6} lg={4}>
                        <Form.Group controlId={`${fieldId}-target-column`}>
                            <Form.Label>Target column</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Column to update"
                                value={(rule as IFeed_Input_Rule_Cell_EnsureLowest).targetColumn || ''}
                                onChange={(event) => updateRule(index, { targetColumn: event.target.value })}
                                required
                            />
                        </Form.Group>
                    </Col>
                </>
            )
        }

        if (rule.kind === 'cell') {
            return (
                <>
                    <Col xs={12} md={6} lg={rule.type === 'replace' ? 2 : 4}>
                        <Form.Group controlId={`${fieldId}-column`}>
                            <Form.Label>Column name</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="For example: title"
                                value={(rule as IFeed_Input_Rule_Cell_Append | IFeed_Input_Rule_Cell_Replace).columnName || ''}
                                onChange={(event) => updateRule(index, { columnName: event.target.value })}
                                required
                            />
                        </Form.Group>
                    </Col>

                    {rule.type === 'append' && (
                        <Col xs={12} md={6} lg={4}>
                            <Form.Group controlId={`${fieldId}-content`}>
                                <Form.Label>Content to append</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Content added after the current value"
                                    value={(rule as IFeed_Input_Rule_Cell_Append).content || ''}
                                    onChange={(event) => updateRule(index, { content: event.target.value })}
                                    required
                                />
                            </Form.Group>
                        </Col>
                    )}

                    {rule.type === 'replace' && (
                        <>
                            <Col xs={12} md={6} lg={2}>
                                <Form.Group controlId={`${fieldId}-from-value`}>
                                    <Form.Label>Find value</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Value to find"
                                        value={(rule as IFeed_Input_Rule_Cell_Replace).fromValue || ''}
                                        onChange={(event) => updateRule(index, { fromValue: event.target.value })}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                            <Col xs={12} md={6} lg={2}>
                                <Form.Group controlId={`${fieldId}-to-value`}>
                                    <Form.Label>Replace with</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="New value"
                                        value={(rule as IFeed_Input_Rule_Cell_Replace).toValue || ''}
                                        onChange={(event) => updateRule(index, { toValue: event.target.value })}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                            <Col xs={12} md={6} lg={2}>
                                <Form.Group controlId={`${fieldId}-encoding`}>
                                    <Form.Label>Transform</Form.Label>
                                    <Form.Select
                                        value={(rule as IFeed_Input_Rule_Cell_Replace).rule || ''}
                                        onChange={(event) => updateRule(index, { rule: event.target.value })}
                                    >
                                        <option value="">None</option>
                                        <option value="decode">Decode</option>
                                        <option value="encode">Encode</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </>
                    )}
                </>
            )
        }

        return (
            <>
                <Col xs={12} md={6} lg={4}>
                    <Form.Group controlId={`${fieldId}-column`}>
                        <Form.Label>Column name</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Column to inspect"
                            value={(rule as IFeed_Input_Rule_Row).columnName || ''}
                            onChange={(event) => updateRule(index, { columnName: event.target.value })}
                            required
                        />
                    </Form.Group>
                </Col>
                <Col xs={12} md={6} lg={4}>
                    <Form.Group controlId={`${fieldId}-from-value`}>
                        <Form.Label>Remove rows matching</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Value to match"
                            value={(rule as IFeed_Input_Rule_Row).fromValue || ''}
                            onChange={(event) => updateRule(index, { fromValue: event.target.value })}
                            required
                        />
                    </Form.Group>
                </Col>
            </>
        )
    }

    return (
        <section className="feed-editor-section mb-0" aria-labelledby="feed-rules-heading">
            <div className="feed-section-heading feed-section-heading-action">
                <div className="d-flex align-items-start gap-3">
                    <span className="feed-section-step">3</span>
                    <div>
                        <h5 id="feed-rules-heading" className="mb-1">Transformation rules</h5>
                        <p className="text-muted small mb-0">Optional steps applied in order before the feed is exported.</p>
                    </div>
                </div>
                <Button
                    type="button"
                    variant="outline-primary"
                    size="sm"
                    onClick={() => onChange([...rules, createRule('column')])}
                >
                    <FaPlus aria-hidden="true" className="me-2" />
                    Add rule
                </Button>
            </div>

            {rules.length === 0 ? (
                <div className="feed-rules-empty">
                    <div className="fw-semibold mb-1">No transformation rules</div>
                    <div className="text-muted small">Source data will be exported without changes.</div>
                </div>
            ) : (
                <div className="feed-builder-list">
                    {rules.map((rule, index) => (
                        <div className="feed-builder-card" key={`rule-${index}`}>
                            <div className="feed-builder-card-header">
                                <div>
                                    <div className="fw-semibold">Rule {index + 1}</div>
                                    <div className="text-muted small">Rules run from top to bottom.</div>
                                </div>
                                <Button
                                    type="button"
                                    variant="link"
                                    className="feed-remove-icon"
                                    onClick={() => handleRemoveRule(index)}
                                    aria-label={`Remove rule ${index + 1}`}
                                    title="Remove rule"
                                >
                                    <FaTrash aria-hidden="true" />
                                </Button>
                            </div>

                            <Row className="g-3">
                                <Col xs={12} sm={6} lg={2}>
                                    <Form.Group controlId={`feed-rule-${index}-kind`}>
                                        <Form.Label>Apply to</Form.Label>
                                        <Form.Select
                                            value={rule.kind}
                                            onChange={(event) => replaceRule(index, createRule(event.target.value as RuleKind))}
                                        >
                                            <option value="column">Column</option>
                                            <option value="cell">Cell</option>
                                            <option value="row">Row</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>

                                <Col xs={12} sm={6} lg={2}>
                                    <Form.Group controlId={`feed-rule-${index}-type`}>
                                        <Form.Label>Operation</Form.Label>
                                        <Form.Select
                                            value={rule.type}
                                            onChange={(event) => replaceRule(index, createRule(rule.kind, event.target.value))}
                                        >
                                            {rule.kind === 'column' && (
                                                <>
                                                    <option value="add">Add</option>
                                                    <option value="copy">Copy</option>
                                                    <option value="copyIfEmpty">Copy if empty</option>
                                                    <option value="remove">Remove</option>
                                                    <option value="replace">Rename</option>
                                                </>
                                            )}
                                            {rule.kind === 'cell' && (
                                                <>
                                                    <option value="append">Append</option>
                                                    <option value="replace">Replace</option>
                                                    <option value="ensureLowest">Use lowest value</option>
                                                </>
                                            )}
                                            {rule.kind === 'row' && <option value="remove">Remove</option>}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>

                                {renderRuleFields(rule, index)}
                            </Row>
                        </div>
                    ))}
                </div>
            )}
        </section>
    )
}

export default FeedRulesForm
